import type { BoundObjectRef } from '@wix/editor-platform-feature-store';
import { OverriddenColorPickerOptions } from '@wix/editor-platform-sdk-types';
import { OverriddenSelectBindingOptions } from '@wix/public-editor-platform-interfaces';

import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../errors';

import { IVirtualManifestBuilderProps, VirtualElementAPIType } from '../api';
import type { VirtualElementProvider, VirtualElement } from '../api';
import { OperationSource } from '../panorama';
import { createOperationContext } from '../api/OperationContext';
import {
  ChangeType,
  IManifestChangeDetection,
  ManifestChangeDetection,
} from '../ManifestChangeDetection';
import { ManifestServiceStats } from '../ManifestServiceStats';

import type { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

export interface IVirtualManifestMetadata {
  overriddenColorPickerOptions?: OverriddenColorPickerOptions;
  overriddenSelectBindingOptions?: OverriddenSelectBindingOptions;
  boundObjectRef?: BoundObjectRef;
}

export enum VirtualManifestStatus {
  Ready = 'ready',
  Const = 'const', // temporary status which never changed
  Invalidated = 'invalidated',
}

export class VirtualManifestsStore {
  #virtualManifests: Map<
    string,
    {
      status: VirtualManifestStatus;
      element: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>;
      factory: () => VirtualElement<VirtualElementAPIType.VirtualManifestAPI>;
      metadata?: IVirtualManifestMetadata;
    }
  > = new Map();

  constructor(
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private readonly virtualElementProvider: VirtualElementProvider,
    private readonly manifestChangeDetection: IManifestChangeDetection,
    private readonly manifestServiceStats: ManifestServiceStats,
  ) {
    this.manifestChangeDetection.subscribe((patch) => {
      if (ManifestChangeDetection.isGlobalPatch(patch)) {
        this.#virtualManifests.forEach((value, contextualVirtualManifestId) => {
          this.manifestServiceStats.cache(
            'VirtualManifestsStore',
            contextualVirtualManifestId,
            'invalidated',
            null,
            'global patch',
          );

          this.#virtualManifests.set(contextualVirtualManifestId, {
            ...value,
            status: VirtualManifestStatus.Invalidated,
          });
        });
      } else if (
        patch.type === ChangeType.Manifest ||
        patch.type === ChangeType.Invalidate
      ) {
        const invalidatedVirtualManifests: string[] = [];

        this.#virtualManifests.forEach(
          ({ element, status }, contextualVirtualManifestId) => {
            if (status === VirtualManifestStatus.Const) {
              return;
            }

            const editorPointers = element.getEditorPointers();

            const isMatched =
              ManifestChangeDetection.isPatchMatchEditorPointers(
                this.editorPointerAPI,
                patch,
                editorPointers,
              );

            if (isMatched) {
              invalidatedVirtualManifests.push(contextualVirtualManifestId);
            }
          },
        );

        invalidatedVirtualManifests.forEach((contextualVirtualManifestId) => {
          this.manifestServiceStats.cache(
            'VirtualManifestsStore',
            contextualVirtualManifestId,
            'invalidated',
            null,
            'manifest change patch',
          );

          this.#virtualManifests.set(contextualVirtualManifestId, {
            ...this.#virtualManifests.get(contextualVirtualManifestId)!,
            status: VirtualManifestStatus.Invalidated,
          });
        });
      }
    });
  }

  getAll() {
    return Object.fromEntries(this.#virtualManifests.entries());
  }

  clean(idx: string) {
    this.#virtualManifests.delete(idx);
  }

  getContextualVirtualManifest(manifestContextId: string) {
    return this.#virtualManifests
      .get(manifestContextId)
      ?.element?.getVirtualManifest();
  }

  getContextualVirtualManifestMetadata(manifestContextId: string) {
    return this.#virtualManifests.get(manifestContextId)?.metadata;
  }

  getContextualVirtualManifestAPI(
    contextualVirtualManifestId: string,
    childPath: number[],
    source?: OperationSource,
  ) {
    const context = this.#virtualManifests.get(contextualVirtualManifestId);

    if (!context) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ManifestContextAPI,
        `contextual manifest does not exist`,
      ).withMessage(contextualVirtualManifestId);
    }

    let element = context.element;

    if (context.status === VirtualManifestStatus.Invalidated) {
      element = context.factory();

      this.#virtualManifests.set(contextualVirtualManifestId, {
        ...context,
        status: VirtualManifestStatus.Ready,
        element,
      });
    }

    return element
      .withOperationContext(createOperationContext(source))
      .withChildPath(childPath)
      .getAPI();
  }

  buildContextualVirtualManifest(
    props: IVirtualManifestBuilderProps,
    metadata?: IVirtualManifestMetadata,
  ) {
    const factory = () => {
      return this.virtualElementProvider.buildVirtualElement(props);
    };

    const element = factory();

    const idx = `${element.getVirtualManifest().virtualManifestId}__${[
      'root',
      ...element.getChildPath(),
    ].join('.')}}`;

    this.manifestServiceStats.cache(
      'VirtualManifestsStore',
      idx,
      'created',
      null,
    );

    this.#virtualManifests.set(idx, {
      status: VirtualManifestStatus.Ready,
      element,
      factory,
      metadata,
    });

    return idx;
  }
}
